import { useState, useEffect } from 'react';
import { SidePanel, Input, Button, Dropdown } from '../ui';
import { Prospect, ProspectStage } from './DealCard';
import AdsReadoutPanel from './AdsReadoutPanel';
import type { AdsStats } from './meta';

const STAGE_OPTIONS: Array<{ value: ProspectStage; label: string }> = [
  { value: 'leads', label: 'Leads' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
];

/** Estimate a fresh card lands with. Mirrors the prospects.deal_value default + insert trigger in the DB. */
const DEFAULT_DEAL_VALUE = 8000;

// tags are no longer editable here; new cards keep the column default.
const emptyForm: Partial<Prospect> = {
  company_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  deal_value: DEFAULT_DEAL_VALUE,
  stage: 'leads',
  source: '',
  gclid: '',
  notes: '',
  tags: [],
};

interface ProspectModalProps {
  isOpen: boolean;
  onClose: () => void;
  prospect?: Prospect | null;
  onSave: (prospect: Partial<Prospect>) => void;
  isLoading: boolean;
  onDelete?: (id: string) => void;
  /** prospects_ads_stats, for pricing what this card will send to Google Ads. */
  adsStats?: AdsStats | null;
}

export default function ProspectModal({ isOpen, onClose, prospect, onSave, isLoading, onDelete, adsStats }: ProspectModalProps) {
  const [formData, setFormData] = useState<Partial<Prospect>>(emptyForm);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(prospect ? { ...prospect, tags: prospect.tags || [] } : emptyForm);
  }, [prospect, isOpen]);

  const handleSubmit = () => {
    onSave(formData);
  };

  const stageName = STAGE_OPTIONS.find((s) => s.value === prospect?.stage)?.label;
  const canSubmit = Boolean(formData.company_name?.trim() && formData.contact_name?.trim());

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      width={460}
      eyebrow={prospect ? `Deal · ${stageName || prospect.stage}` : 'New Deal'}
      title={prospect ? 'Edit Deal' : 'Add Deal'}
      footer={
        <>
          <Button icon="check" onClick={handleSubmit} disabled={isLoading || !canSubmit}>
            {isLoading ? 'Saving...' : prospect ? 'Save Deal' : 'Add Deal'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {prospect && onDelete ? (
            <>
              <span style={{ flex: 1 }} />
              <Button
                variant="ghost"
                icon="trash-2"
                style={{ color: 'var(--danger-500)' }}
                onClick={() => onDelete(prospect.id)}
              >
                Delete
              </Button>
            </>
          ) : null}
        </>
      }
    >
      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {prospect ? (
          <AdsReadoutPanel
            card={{ ...prospect, deal_value: Number(formData.deal_value) || 0, contact_email: formData.contact_email ?? null, gclid: formData.gclid || null }}
            closeRate={adsStats?.close_rate ?? null}
            closeRateIsOverride={adsStats?.close_rate_override != null}
          />
        ) : null}

        <Input
          label="Company"
          icon="building-2"
          placeholder="Company name..."
          value={formData.company_name || ''}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
        />

        {/* Long values (email, GCLID) stack on phones instead of truncating at half width */}
        <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '12px' }}>
          <Input
            label="Contact"
            icon="user"
            placeholder="Full name..."
            value={formData.contact_name || ''}
            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
          />
          <Input
            label="Email"
            icon="mail"
            type="email"
            placeholder="name@company.com"
            value={formData.contact_email || ''}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Input
            label="Phone"
            type="tel"
            placeholder="Phone number..."
            value={formData.contact_phone || ''}
            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
          />
          <Input
            label="Deal Value"
            icon="circle-dollar-sign"
            type="number"
            min="0"
            placeholder="0"
            value={formData.deal_value ?? 0}
            onChange={(e) => setFormData({ ...formData, deal_value: Number(e.target.value) })}
          />
        </div>

        <Dropdown
          label="Stage"
          options={STAGE_OPTIONS}
          value={formData.stage || 'leads'}
          onChange={(e) => setFormData({ ...formData, stage: e.target.value as ProspectStage })}
          placeholder="Select stage"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '12px' }}>
          <Input
            label="Source"
            icon="globe"
            placeholder="Referral, Website..."
            value={formData.source || ''}
            onChange={(e) => setFormData({ ...formData, source: e.target.value })}
          />
          <Input
            label="GCLID"
            icon="link"
            placeholder="Google Click ID..."
            value={formData.gclid || ''}
            onChange={(e) => setFormData({ ...formData, gclid: e.target.value })}
          />
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>
            Notes
          </div>
          <textarea
            className="ck-textarea"
            rows={4}
            placeholder="Deal context, next steps..."
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </div>
      </div>
    </SidePanel>
  );
}
